import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { useUser } from "src/contexts/user-context";
import {
  useRequestAdminEntitlementMutation,
  useAdminEntitlementStatusQuery,
} from "src/graphql";
import * as storage from "src/utils/storage";

// Enum values matching the GraphQL schema
export const AdminEntitlementRequestStatus = {
  Granted: "Granted",
  NotRequired: "NotRequired",
  Requested: "Requested",
  Error: "Error",
} as const;

export const AdminEntitlementStatus = {
  Pending: "Pending",
  Approved: "Approved",
  Denied: "Denied",
  Canceled: "Cancelled",
  NotFound: "NotFound",
  NotConfigured: "NotConfigured",
  Error: "Error",
} as const;

type AdminEntitlementStatusType =
  (typeof AdminEntitlementStatus)[keyof typeof AdminEntitlementStatus];

// Internal status used by the hook (includes "idle" for initial state)
export type EntitlementRequestStatus = "idle" | AdminEntitlementStatusType;

export interface EntitlementRequestState {
  status: EntitlementRequestStatus;
  taskId?: string;
  createdAt?: string;
  error?: string;
}

export interface UseAdminEntitlementRequestReturn {
  requestState: EntitlementRequestState;
  isLoading: boolean;
  isAwaitingResolution: boolean;
  submitRequest: (reason: string) => Promise<void>;
  checkStatus: () => Promise<void>;
  resetState: () => void;
}

const STORAGE_KEY_PREFIX = "entitlement-request-";

function getStorageKey(workspaceId: number | string): string {
  return `${STORAGE_KEY_PREFIX}${workspaceId}`;
}

function getStoredTaskId(workspaceId: number | string): string | null {
  try {
    return storage.load(getStorageKey(workspaceId));
  } catch {
    return null;
  }
}

function storeTaskId(workspaceId: number | string, taskId: string): void {
  try {
    storage.save(getStorageKey(workspaceId), taskId);
  } catch {
    // Ignore storage errors
  }
}

function clearStoredTaskId(workspaceId: number | string): void {
  try {
    storage.remove(getStorageKey(workspaceId));
  } catch {
    // Ignore storage errors
  }
}

export function useAdminEntitlementRequest(
  workspaceId?: number | string,
): UseAdminEntitlementRequestReturn {
  const { user } = useUser();
  const effectiveWorkspaceId = workspaceId ?? user?.current_workspace_id;

  const [requestState, setRequestState] = useState<EntitlementRequestState>({
    status: "idle",
  });

  // Track when we're waiting for a terminal state (approved/denied/canceled/error)
  const [isAwaitingResolution, setIsAwaitingResolution] = useState(false);

  // GraphQL mutation for submitting requests
  const requestMutation = useRequestAdminEntitlementMutation({
    onSuccess: (data) => {
      const result = data.requestAdminEntitlement;
      if (
        // "Granted" means the user already has the entitlement via ConductorOne.
        // "NotRequired" means ConductorOne is not configured or the feature flag
        // is off for this workspace, so no grant is needed.
        // In both cases, the user can proceed without requesting/waiting for approval.
        result.status === AdminEntitlementRequestStatus.Granted ||
        result.status === AdminEntitlementRequestStatus.NotRequired
      ) {
        setRequestState({ status: AdminEntitlementStatus.Approved });
        setIsAwaitingResolution(false);
      } else if (
        result.status === AdminEntitlementRequestStatus.Requested &&
        result.taskId
      ) {
        if (effectiveWorkspaceId) {
          storeTaskId(effectiveWorkspaceId, result.taskId);
        }
        setRequestState({
          status: AdminEntitlementStatus.Pending,
          taskId: result.taskId,
          createdAt: new Date().toISOString(),
        });
        // Keep awaiting resolution - we're now pending
      } else {
        setRequestState({
          status: AdminEntitlementStatus.Error,
          error: result.message ?? "Failed to submit request",
        });
        setIsAwaitingResolution(false);
      }
    },
    onError: (error) => {
      setRequestState({
        status: AdminEntitlementStatus.Error,
        error: error instanceof Error ? error.message : "Unknown error",
      });
      setIsAwaitingResolution(false);
    },
  });

  // Determine query input based on stored task ID or workspace ID
  const storedTaskId = effectiveWorkspaceId
    ? getStoredTaskId(effectiveWorkspaceId)
    : null;

  const statusQueryInput = useMemo(() => {
    if (storedTaskId) return { taskId: storedTaskId };
    if (effectiveWorkspaceId)
      return { workspaceId: Number(effectiveWorkspaceId) };
    return null;
  }, [storedTaskId, effectiveWorkspaceId]);

  // GraphQL query for checking status (manual refetch)
  const statusQuery = useAdminEntitlementStatusQuery(
    { input: statusQueryInput! },
    {
      enabled: false, // Manual trigger only
      onSuccess: (data) => {
        const result = data.adminEntitlementStatus;

        // If approved or denied, clear stored task ID and stop awaiting
        if (
          effectiveWorkspaceId &&
          (result.status === AdminEntitlementStatus.Approved ||
            result.status === AdminEntitlementStatus.Denied ||
            result.status === AdminEntitlementStatus.Canceled)
        ) {
          clearStoredTaskId(effectiveWorkspaceId);
          setIsAwaitingResolution(false);
        }

        // If we found a task ID from the search, store it
        if (result.taskId && effectiveWorkspaceId && !storedTaskId) {
          storeTaskId(effectiveWorkspaceId, result.taskId);
        }

        setRequestState({
          status: result.status as AdminEntitlementStatusType,
          taskId: result.taskId ?? undefined,
          createdAt: result.createdAt ?? undefined,
        });
      },
      onError: (error) => {
        setRequestState({
          status: AdminEntitlementStatus.Error,
          error: error instanceof Error ? error.message : "Unknown error",
        });
        setIsAwaitingResolution(false);
      },
    },
  );

  // Check for stored task ID on mount
  useEffect(() => {
    if (effectiveWorkspaceId) {
      const taskId = getStoredTaskId(effectiveWorkspaceId);
      if (taskId) {
        setRequestState({
          status: AdminEntitlementStatus.Pending,
          taskId,
        });
      }
    }
  }, [effectiveWorkspaceId]);

  // Poll for status updates while awaiting resolution
  useEffect(() => {
    if (
      !isAwaitingResolution ||
      requestState.status !== AdminEntitlementStatus.Pending
    ) {
      return;
    }

    const interval = setInterval(() => {
      statusQuery.refetch();
    }, 5000); // Poll every 5 seconds

    return () => clearInterval(interval);
  }, [isAwaitingResolution, requestState.status, statusQuery]);

  const submitRequest = useCallback(
    async (reason: string) => {
      if (!effectiveWorkspaceId) {
        setRequestState({
          status: AdminEntitlementStatus.Error,
          error: "No workspace ID",
        });
        return;
      }

      setIsAwaitingResolution(true);
      await requestMutation.mutateAsync({
        input: {
          workspaceId: Number(effectiveWorkspaceId),
          reason,
        },
      });
    },
    [effectiveWorkspaceId, requestMutation],
  );

  // Use ref to stabilize the refetch function reference
  const refetchRef = useRef(statusQuery.refetch);
  refetchRef.current = statusQuery.refetch;

  const checkStatus = useCallback(async () => {
    if (!statusQueryInput) return;
    await refetchRef.current();
  }, [statusQueryInput]);

  const resetState = useCallback(() => {
    if (effectiveWorkspaceId) {
      clearStoredTaskId(effectiveWorkspaceId);
    }
    setRequestState({ status: "idle" });
    setIsAwaitingResolution(false);
  }, [effectiveWorkspaceId]);

  return {
    requestState,
    isLoading: requestMutation.isLoading || statusQuery.isFetching,
    isAwaitingResolution,
    submitRequest,
    checkStatus,
    resetState,
  };
}
